// apps/web/src/components/perfis/perfil-dialogs.tsx — quick 261006-lf9.
// Criar, editar e excluir perfil. O conteúdo é puro (props); os wrappers cuidam
// do estado e chamam apiMutate (cookie + CSRF). Nenhum UUID aparece como texto.
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  FormDialog,
  FormDialogBody,
  FormDialogContent,
  FormDialogFooter,
  FormDialogHeader,
  FormDialogTitle,
} from '../ui/form-dialog';
import { mensagemDoServidor, type PerfilResumo } from './comum';
import { CampoSelect } from '../ui/campo-select';

const CAMPO =
  'min-h-[44px] w-full rounded border border-input bg-background px-4 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-60';
const BOTAO_PRIMARIO =
  'min-h-[44px] rounded bg-primary px-4 text-sm font-semibold text-primary-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50';
const BOTAO_SECUNDARIO =
  'min-h-[44px] rounded border border-border px-4 text-sm font-semibold text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50';

export const MOTIVO_SISTEMA_RENOMEAR = 'Perfis do sistema não podem ser renomeados.';
export const MOTIVO_SISTEMA_EXCLUIR = 'Perfis do sistema não podem ser excluídos.';

export function motivoDeNaoExcluir(perfil: Pick<PerfilResumo, 'sistema' | 'usuariosAtivos' | 'usuariosInativos'>): string | null {
  if (perfil.sistema) return MOTIVO_SISTEMA_EXCLUIR;
  const pessoas = perfil.usuariosAtivos + perfil.usuariosInativos;
  if (pessoas > 0) {
    return `Este perfil ainda está atribuído a ${pessoas} ${pessoas === 1 ? 'pessoa' : 'pessoas'}. Troque o perfil delas antes de excluir.`;
  }
  return null;
}

export interface PerfilFormConteudoProps {
  readonly modo: 'criar' | 'editar';
  readonly nome: string;
  readonly descricao: string;
  readonly perfilBaseId: string;
  readonly perfisBase: readonly Pick<PerfilResumo, 'id' | 'nome'>[];
  readonly sistema: boolean;
  readonly erro?: string | null;
  readonly enviando: boolean;
  readonly onNome: (valor: string) => void;
  readonly onDescricao: (valor: string) => void;
  readonly onPerfilBase: (valor: string) => void;
  readonly onSubmit: (evento?: FormEvent) => void;
  readonly onCancelar: () => void;
}

export function PerfilFormConteudo({
  modo, nome, descricao, perfilBaseId, perfisBase, sistema, erro, enviando, onNome, onDescricao, onPerfilBase, onSubmit, onCancelar,
}: PerfilFormConteudoProps): ReactElement {
  const nomeValido = nome.trim().length >= 2 && nome.trim().length <= 60;
  return (
    <form onSubmit={(evento) => onSubmit(evento)} aria-busy={enviando}>
      <FormDialogBody className="space-y-4">
        {erro ? <div className="rounded border border-danger p-4 text-sm text-danger" role="alert">{erro}</div> : null}
        <div className="space-y-2">
          <label htmlFor="perfil-nome" className="block text-sm font-semibold text-foreground">Nome</label>
          <input
            id="perfil-nome"
            type="text"
            value={nome}
            maxLength={60}
            required
            disabled={enviando || (modo === 'editar' && sistema)}
            onChange={(evento) => onNome(evento.target.value)}
            className={CAMPO}
          />
          {modo === 'editar' && sistema ? <p className="text-sm text-muted-foreground">{MOTIVO_SISTEMA_RENOMEAR}</p> : null}
        </div>
        <div className="space-y-2">
          <label htmlFor="perfil-descricao" className="block text-sm font-semibold text-foreground">Descrição</label>
          <input
            id="perfil-descricao"
            type="text"
            value={descricao}
            maxLength={240}
            disabled={enviando}
            onChange={(evento) => onDescricao(evento.target.value)}
            className={CAMPO}
          />
        </div>
        {modo === 'criar' ? (
          <div className="space-y-2">
            <label htmlFor="perfil-base" className="block text-sm font-semibold text-foreground">Copiar permissões de (opcional)</label>
            <CampoSelect
              id="perfil-base"
              value={perfilBaseId}
              disabled={enviando}
              onValueChange={onPerfilBase}
              options={[{ value: '', label: 'Começar sem permissões' }, ...perfisBase.map((perfil) => ({ value: perfil.id, label: perfil.nome }))]}
            />
          </div>
        ) : null}
      </FormDialogBody>
      <FormDialogFooter>
        <button type="button" onClick={onCancelar} disabled={enviando} className={BOTAO_SECUNDARIO}>Cancelar</button>
        <button type="submit" disabled={enviando || !nomeValido} className={BOTAO_PRIMARIO}>
          {enviando ? 'Salvando…' : modo === 'criar' ? 'Criar perfil' : 'Salvar'}
        </button>
      </FormDialogFooter>
    </form>
  );
}

export interface ExcluirPerfilConteudoProps {
  readonly perfil: Pick<PerfilResumo, 'nome' | 'sistema' | 'usuariosAtivos' | 'usuariosInativos'>;
  readonly erro?: string | null;
  readonly enviando: boolean;
  readonly onConfirmar: () => void;
  readonly onCancelar: () => void;
}

export function ExcluirPerfilConteudo({ perfil, erro, enviando, onConfirmar, onCancelar }: ExcluirPerfilConteudoProps): ReactElement {
  const motivo = motivoDeNaoExcluir(perfil);
  return (
    <form onSubmit={(evento) => { evento.preventDefault(); onConfirmar(); }} aria-busy={enviando}>
      <FormDialogBody className="space-y-4">
        {erro ? <div className="rounded border border-danger p-4 text-sm text-danger" role="alert">{erro}</div> : null}
        <p className="text-sm text-foreground">
          Excluir o perfil <strong className="font-semibold">{perfil.nome}</strong>? As permissões dele serão removidas. Esta ação fica registrada na auditoria.
        </p>
        {motivo ? <p className="text-sm text-muted-foreground" data-testid="excluir-perfil-motivo">{motivo}</p> : null}
      </FormDialogBody>
      <FormDialogFooter>
        <button type="button" onClick={onCancelar} disabled={enviando} className={BOTAO_SECUNDARIO}>Cancelar</button>
        <button type="submit" disabled={enviando || motivo !== null} className={BOTAO_PRIMARIO}>
          {enviando ? 'Excluindo…' : 'Excluir perfil'}
        </button>
      </FormDialogFooter>
    </form>
  );
}

export interface PerfilFormDialogProps {
  readonly aberto: boolean;
  /** Perfil em edição; nulo para criar. */
  readonly perfil: PerfilResumo | null;
  readonly perfis: readonly PerfilResumo[];
  readonly onClose: () => void;
  readonly onSalvo: (resumo: PerfilResumo, modo: 'criar' | 'editar') => void;
}

export function PerfilFormDialog({ aberto, perfil, perfis, onClose, onSalvo }: PerfilFormDialogProps): ReactElement | null {
  const modo = perfil ? 'editar' : 'criar';
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [perfilBaseId, setPerfilBaseId] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setNome(perfil?.nome ?? '');
    setDescricao(perfil?.descricao ?? '');
    setPerfilBaseId('');
    setErro(null);
  }, [aberto, perfil]);

  if (!aberto) return null;

  async function enviar(evento?: FormEvent): Promise<void> {
    evento?.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      const resposta = perfil
        ? await apiMutate(`/api/v1/authorization/roles/${perfil.id}`, { method: 'PATCH', body: { nome: nome.trim(), descricao: descricao.trim() } })
        : await apiMutate('/api/v1/authorization/roles', {
            method: 'POST',
            body: { nome: nome.trim(), descricao: descricao.trim(), ...(perfilBaseId ? { perfilBaseId } : {}) },
          });
      if (!resposta.ok) {
        setErro(mensagemDoServidor(resposta.status, await resposta.json().catch(() => ({}))));
        return;
      }
      onSalvo((await resposta.json()) as PerfilResumo, modo);
    } catch {
      setErro('Não foi possível concluir a operação. Nada foi alterado — tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <FormDialog open={aberto} onOpenChange={(proximo) => { if (!proximo) onClose(); }}>
      <FormDialogContent aria-describedby={undefined}>
        <FormDialogHeader><FormDialogTitle>{perfil ? 'Editar nome e descrição' : 'Novo perfil'}</FormDialogTitle></FormDialogHeader>
        <PerfilFormConteudo
          modo={modo}
          nome={nome}
          descricao={descricao}
          perfilBaseId={perfilBaseId}
          perfisBase={perfis}
          sistema={perfil?.sistema ?? false}
          erro={erro}
          enviando={enviando}
          onNome={setNome}
          onDescricao={setDescricao}
          onPerfilBase={setPerfilBaseId}
          onSubmit={(evento) => void enviar(evento)}
          onCancelar={onClose}
        />
      </FormDialogContent>
    </FormDialog>
  );
}

export interface ExcluirPerfilDialogProps {
  readonly perfil: PerfilResumo | null;
  readonly onClose: () => void;
  readonly onExcluido: (perfil: PerfilResumo) => void;
}

export function ExcluirPerfilDialog({ perfil, onClose, onExcluido }: ExcluirPerfilDialogProps): ReactElement | null {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setErro(null);
  }, [perfil]);

  if (!perfil) return null;
  const alvo = perfil;

  async function confirmar(): Promise<void> {
    setEnviando(true);
    setErro(null);
    try {
      const resposta = await apiMutate(`/api/v1/authorization/roles/${alvo.id}`, { method: 'DELETE' });
      if (!resposta.ok) {
        setErro(mensagemDoServidor(resposta.status, await resposta.json().catch(() => ({}))));
        return;
      }
      onExcluido(alvo);
    } catch {
      setErro('Não foi possível concluir a operação. Nada foi alterado — tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <FormDialog open onOpenChange={(proximo) => { if (!proximo) onClose(); }}>
      <FormDialogContent aria-describedby={undefined}>
        <FormDialogHeader><FormDialogTitle>Excluir perfil</FormDialogTitle></FormDialogHeader>
        <ExcluirPerfilConteudo perfil={alvo} erro={erro} enviando={enviando} onConfirmar={() => void confirmar()} onCancelar={onClose} />
      </FormDialogContent>
    </FormDialog>
  );
}
